import React, { useEffect, useState } from "react";

import { SelectTabData, SelectTabEvent, Tab, TabList } from "@fluentui/react-components";
import { Home20Filled, WindowSettings20Filled } from "@fluentui/react-icons";

import Home from "../../modules/home";
import FeedManager from "../../modules/feedManager";

import { getApiKeyFromWorksheet, initializeOffice } from "./config";

import { saveSentValues } from "./saveState";
import { loadSentValues } from "./loadState";

import { InsertUpdatedSecurityValues } from "../../service/store/types";
import { selectAutoUpdateEnabled } from "../../modules/feedManager/store";
import { useAppDispatch } from "../../store";
import { useSelector } from "react-redux";
import { sendSecurityPrices } from "../../service/store/asyncThunks";
import {
  addSecurityToStagingArea,
  selectSecuritiesValues,
  setQuoteFeedOfSecurityInPricesSentAsNone,
} from "../../service/store";
import { selectIsConnected, updateConnectionTime } from "./statusBar/store";
import { registerEventHandlers } from "../../registerEventHandlers";
import { useStyles } from "./App.styles";
import { LocalStorageKeys } from "../../config";
import { PricesResendManager } from "../../classes/pricesResendManager";

export enum TabId {
  Home = "homeTab",
  FeedManager = "feedManagerTab",
}

initializeOffice();

const TAB_SHOWED = {
  [TabId.Home]: (onContinue: () => void) => <Home onContinue={onContinue} />,
  [TabId.FeedManager]: () => <FeedManager />,
};

const App = () => {
  const [selectedTab, setSelectedTab] = useState(TabId.Home);

  const styles = useStyles();

  const dispatch = useAppDispatch();

  const securitiesValuesState = useSelector(selectSecuritiesValues);
  const autoUpdateState = useSelector(selectAutoUpdateEnabled);
  const isConnectionEstablished = useSelector(selectIsConnected);
  const handleSaveSecurityInStaging = async (data: InsertUpdatedSecurityValues) => {
    const { row, securityProducerId } = data;

    if (autoUpdateState && isConnectionEstablished) {
      await handleUpdateValues({ securityId: securityProducerId, updatedRow: row, data });
    } else {
      dispatch(addSecurityToStagingArea(data));
    }

    dispatch(updateConnectionTime());
  };

  const handleUpdateValues = async ({
    securityId,
    updatedRow,
    data,
  }: {
    securityId: string;
    updatedRow: number;
    data?: InsertUpdatedSecurityValues;
  }) => {
    if (data && data.quoteFeedId === "none") return;

    await dispatch(sendSecurityPrices({ securityId, updatedRow, data }));
  };

  const handleSetQuoteFeedAsNone = ({ securityProducerId }: { securityProducerId: string }) =>
    dispatch(setQuoteFeedOfSecurityInPricesSentAsNone({ securityProducerId }));

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => setSelectedTab(data.value as TabId);

  useEffect(() => {
    registerEventHandlers({
      onSaveSecurityInStaging: handleSaveSecurityInStaging,
      onUploadData: handleUpdateValues,
      onSetQuoteFeedAsNone: handleSetQuoteFeedAsNone,
    });
  }, [autoUpdateState, isConnectionEstablished]);

  useEffect(() => {
    loadSentValues(dispatch);
    getApiKeyFromWorksheet(dispatch, setSelectedTab);
    localStorage.setItem(LocalStorageKeys.TOKEN, null);
    localStorage.setItem(LocalStorageKeys.ENVIRONMENT, null);
  }, []);

  useEffect(() => {
    saveSentValues(securitiesValuesState);
  }, [securitiesValuesState]);

  return (
    <div className={styles.root}>
      <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
        <Tab icon={<Home20Filled />} value={TabId.Home}>
          Home
        </Tab>
        <Tab icon={<WindowSettings20Filled />} value={TabId.FeedManager}>
          Feed Manager
        </Tab>
      </TabList>

      {TAB_SHOWED[selectedTab](() => setSelectedTab(TabId.FeedManager))}
    </div>
  );
};

export default App;
